import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons";
import { useStyles } from "@/hooks/useStyles";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

const appUrl = import.meta.env.DEV ? "http://localhost:3000" : window.location.origin.replace("uploader", "app");
const appLabel = import.meta.env.DEV ? "localhost:3000" : window.location.host.replace("uploader", "app");

export type LoginChoiceProps = {
  /** Starts the patient login flow. */
  onConnect: () => void;
};

/**
 * Landing step: sends patients into the login flow and practitioners to the web app.
 */
export const LoginChoice: React.FC<LoginChoiceProps> = ({ onConnect }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.choices}>
      <div className={styles.column}>
        <Typography variant="h4" className={styles.columnTitle}>
          {t("pages.login.patientTitle")}
        </Typography>
        <Button variant="contained" className={styles.connectionButton} onClick={onConnect}>
          {t("pages.login.connection")}
        </Button>
      </div>

      <div className={styles.column}>
        <Typography variant="h4" className={styles.columnTitle}>
          {t("pages.login.practitionerTitle")}
        </Typography>
        <Typography variant="bodySmall" className={styles.instructions}>
          {t("pages.login.practitionerInstructions")}
        </Typography>
        <Button variant="text" className={styles.linkButton} component="a" target="_blank" href={appUrl}>
          {appLabel}
          <Icons.externalLink className={styles.badgeLink} />
        </Button>
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  choices: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "center",
    rowGap: theme.spacing(24),
    columnGap: theme.spacing(48),
    flexWrap: "wrap",
  }),
  column: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(8),
    maxWidth: theme.spacing(256),
  }),
  columnTitle: css({
    color: theme.palette.common.white,
    textAlign: "center",
  }),
  connectionButton: css({
    padding: theme.spacing(4, 8),
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    alignSelf: "center",
  }),
  instructions: css({
    color: theme.palette.common.white,
    textAlign: "center",
    flex: "1 1 0",
    whiteSpace: "pre-line",
    maxWidth: "80%",
  }),
  linkButton: css({
    color: theme.palette.common.white,
  }),
  badgeLink: css({
    cursor: "pointer",
    color: theme.palette.common.white,
    marginLeft: theme.spacing(2),
    width: FontSizes.h6,
    height: FontSizes.h6,
  }),
});
